import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { settingsService } from "services/settings/settings.service"

import { LiveChatSettings } from "types/api"

import { INITIAL_FORM } from "./constants"
import { FormState, SetFormField } from "./types"
import { buildPayload, settingsToForm } from "./utils"
import { hasErrors, validateForm } from "./validation"

export interface UseSettingsResult {
  form: FormState
  loaded: LiveChatSettings | null
  loading: boolean
  saving: boolean
  setField: SetFormField
  handleSave: () => Promise<void>
  /** Reloads from the API — used after an external reset. */
  refetch: () => Promise<void>
}

export function useSettings(): UseSettingsResult {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loaded, setLoaded] = useState<LiveChatSettings | null>(null)
  const [form, setForm] = useState<FormState>(INITIAL_FORM)

  const fetchSettings = useCallback(async () => {
    setLoading(true)
    try {
      const settings = await settingsService.getLiveChatSettings()
      if (!settings) {
        toast.error(t("settings.liveChat.loadFailed") as string)
        return
      }
      setLoaded(settings)
      setForm(settingsToForm(settings))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const setField = useCallback<SetFormField>(
    (key, value) => setForm((prev) => ({ ...prev, [key]: value })),
    []
  )

  const handleSave = useCallback(async () => {
    // The Save button is already disabled while invalid; this catches the race.
    if (hasErrors(validateForm(form))) {
      toast.error(t("settings.liveChat.validation.blocked") as string)
      return
    }

    setSaving(true)
    try {
      const success = await settingsService.updateLiveChatSettings(buildPayload(form, loaded))
      toast[success ? "success" : "error"](
        t(success ? "settings.liveChat.updated" : "settings.liveChat.updateFailed") as string
      )
      if (success) await fetchSettings()
    } finally {
      setSaving(false)
    }
  }, [form, loaded, fetchSettings, t])

  // Resetting goes through the shared ResetToDefaultButton, which confirms first.
  return { form, loaded, loading, saving, setField, handleSave, refetch: fetchSettings }
}
